import React, { useState, useMemo, useEffect, useRef, useCallback, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { PatientDataContext } from '../../../context/PatientDataContext';
import { CheckIcon, XIcon, RefreshIcon, PencilIcon, SaveIcon, FastForwardIcon } from '@heroicons/react/solid';
import './styles/ReviewDocuments.css';
import { db, auth } from '../../../firebase';
import { TriggerRenderDocTemplate } from '../../../utils/TriggerRenderDocTemplate';
import { InitialWarningDialog } from './components/InitialWarningDialog';
import { doc, updateDoc, deleteField, serverTimestamp } from 'firebase/firestore';
import WarningBar from './components/WarningBar';
import DocumentReviewHeader from './components/DocumentReviewHeader';
import { decodeHtmlEntities } from './functions/htmlEncoders';
import { handlePlainTextPaste, suppressFormattingShortcuts } from './functions/contentEditableUtils';
import { useApprovalTracking } from '../../../hooks/useApprovalTracking';
import { usePendingApprovals } from '../../../hooks/usePendingApprovals';
import { finalizeSectionReview } from '../../../utils/sectionReviewFinalization';
import { computeUnpersistedApprovals } from '../../../utils/approvalStateUtils';
import { appendReportAuditEntry } from '../../../utils/reportAuditHelpers';
import { toast } from 'react-toastify';
import { renderModelInput as _renderModelInput } from '../../../utils/renderModelInput';
import {
  emitParagraphApproval,
  emitParagraphEdited,
  emitParagraphReset,
} from '../../../analytics/reportEvents';

export default function ReviewDocumentQBTest() {

  const { patientId } = useParams();

  const MODULE_NUMBER = 8
  const MODULE_PREFIX_CODE = "qbTest"
  const MODULE_TITLE = "QbTest"

  const navigate = useNavigate();
  const { patientDataModel, fetchPatientData, clearPatientData } = useContext(PatientDataContext);

  useEffect(() => {
    // Force fresh fetch
    fetchPatientData(patientId);
  }, [patientId, fetchPatientData]);

  const docStructure = useMemo(() => ({
    "QbTest": {
      "1": { "title": "Conclusions", "code": "par_qbTestConclusions" },
    },
  }), []);

  const REPORT_DATA_OUTPUTS = useMemo(() =>
    patientDataModel?.dataModel?.['6_manageReports']?.sections?.[`${MODULE_NUMBER}`]?.outputs || {},
    [patientDataModel]);

  // The paragraph edit history, read from the SAME already-loaded section as the
  // outputs above. It is what lets the adoption numbers (how much of the AI draft
  // survived) be computed at the moment of an edit rather than by reading the
  // report back out of Firestore later - and because this object is already in
  // memory to render the page, it costs no additional read.
  //
  // In-memory means it can lag a write made elsewhere in the same instant. That
  // only matters for a paragraph's FIRST edit, and only if another session edited
  // it seconds earlier; once the earliest entry exists it stays the earliest
  // forever. The nightly reconciliation reads authoritative Firestore, so a
  // disagreement surfaces there rather than going unnoticed.
  const REPORT_EDIT_HISTORY = useMemo(() =>
    patientDataModel?.dataModel?.['6_manageReports']?.sections?.[`${MODULE_NUMBER}`]?.editHistory || {},
    [patientDataModel]);

  const [state, setState] = useState({
    selectedCode: null,
    textareaContent: null,
    isSidePanelOpen: false,
    modelInputsFirestore: null,
    clinicianInputs: null,
    clinicianDecisions: null,
    checkedExamples: null,
    patientInputs: null,
    informantInputs: null
  });

  const [isEditing, setIsEditing] = useState(false);
  const [actionPanelPosition, setActionPanelPosition] = useState({ top: 0, left: 0 });

  const {
    totalParagraphs, reviewedCodes, isFullyApproved,
    showWarning, addReviewedCode, removeReviewedCode,
  } = useApprovalTracking({
    patientId,
    moduleNumber: MODULE_NUMBER,
    docStructure,
    reportDataOutputs: REPORT_DATA_OUTPUTS,
  });

  // Tracks in-flight per-paragraph approval writes so Save & Exit can wait for
  // them to commit before deriving and persisting the section approval flag.
  const { track: trackApproval, settleAll: settlePendingApprovals } = usePendingApprovals();

  const handleParagraphClick = (event, code, content) => {
    // Reset edit states
    setIsEditing(false);

    const element = event.currentTarget;
    const containerRect = documentContainerRef.current.getBoundingClientRect();
    const paragraphRect = element.getBoundingClientRect();

    // Calculate position for action panel
    setActionPanelPosition({
      top: paragraphRect.bottom - containerRect.top + documentContainerRef.current.scrollTop - 15,
      left: paragraphRect.right - containerRect.left - 275
    });

    if (!state.modelInputsFirestore) {
      console.error('modelInputsFirestore is not populated');
      return;
    }

    if (code && state.modelInputsFirestore[code]) {
      setState(prevState => ({
        ...prevState,
        selectedCode: code,
        textareaContent: content || ' ', // always having at least a non-breaking space
        article: state.modelInputsFirestore[code]["Article"],
        clinicianInputs: state.modelInputsFirestore[code]["Clinician Notes"],
        // clinicianDecisions: state.modelInputsFirestore[code]["Clinician Decisions"],
        patientInputs: state.modelInputsFirestore[code]["Patient Prefill"],
        // informantInputs: state.modelInputsFirestore[code]["Informant Prefill"],
        isSidePanelOpen: true
      }));
    } else {
      console.error('No matching input found for the code:', code);
    }
  };

  // Wrap shared renderModelInput with articleLinks enabled for this page's
  // ThinkDivergent evidence-based article sources (rendered as clickable links)
  const renderModelInput = (role, inputs) => _renderModelInput(role, inputs, { articleLinks: true });

  // Update the Section component to only render paragraphs with content
  const Section = ({ title, paragraphs }) => {
    // First filter out paragraphs that don't have content in REPORT_DATA_OUTPUTS
    const availableParagraphs = Object.entries(paragraphs).filter(
      ([_, { code }]) => REPORT_DATA_OUTPUTS[code] !== undefined && REPORT_DATA_OUTPUTS[code] !== ""
    );

    // Only render the section if it has at least one available paragraph
    if (availableParagraphs.length === 0) return null;

    return (
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-4 text-blue-500">{title}</h2>

        {availableParagraphs.map(([key, { title: subTitle, code }]) => {
          const content = REPORT_DATA_OUTPUTS[code];
          const decodedContent = decodeHtmlEntities(content); // Decode for display        
          return (
            <div key={key} className="mb-4">
              {subTitle && (
                <h3 className="font-semibold mb-2 text-gray-900">
                  {subTitle}
                </h3>
              )}
              <div
                data-paragraph-container
                className={`
                relative text-sm font-medium p-2 rounded-lg
                transition-all duration-200
                ${isEditing && state.selectedCode === code
                    ? 'bg-amber-100 z-20' // Edit mode background
                    : reviewedCodes.has(code)
                      ? 'bg-green-600/30 hover:bg-green-600/40 approved-paragraph'
                      : 'bg-violet-100 hover:bg-violet-200'
                  }
                ${state.selectedCode === code
                    ? 'ring ring-violet-800'
                    : ''
                  }
                ${!isEditing ? 'cursor-pointer' : ''}
              `}
                id={code}
                onClick={(e) => {
                  if (!isEditing) {
                    handleParagraphClick(e, code, decodedContent);
                  }
                }}
              >
                <p
                  ref={editableContentRef}
                  className="text-gray-700 leading-relaxed whitespace-pre-wrap"
                  contentEditable={isEditing && state.selectedCode === code}
                  onKeyDown={suppressFormattingShortcuts}
                  onInput={(e) => {
                    if (isEditing && state.selectedCode === code) {
                      let newContent = e.target.innerText;
                      newContent = newContent.replace(/\n/g, '\n');

                      if (REPORT_DATA_OUTPUTS) {
                        REPORT_DATA_OUTPUTS[code] = newContent;
                      }
                    }
                  }}
                  onPaste={handlePlainTextPaste}
                  suppressContentEditableWarning={true}
                >
                  {decodedContent}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Add a ref for the paragraph content
  const editableContentRef = useRef(null);

  // Update handleStartEdit to focus the content
  const handleStartEdit = () => {
    if (state.selectedCode) {
      // Store the current content before starting edit
      setPreEditContent(prev => ({
        ...prev,
        [state.selectedCode]: REPORT_DATA_OUTPUTS[state.selectedCode] || ''
      }));
    }
    setIsEditing(true);
    // Use setTimeout to ensure the contentEditable is enabled before focusing
    setTimeout(() => {
      const paragraphElement = document.querySelector(`#${state.selectedCode} p`);
      if (paragraphElement) {
        paragraphElement.focus();
        // Create a range and selection to place cursor at the end
        const range = document.createRange();
        const selection = window.getSelection();
        range.selectNodeContents(paragraphElement);
        range.collapse(false); // false means collapse to end
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }, 0);
  };

  const handleSaveEdit = () => {
    if (state.selectedCode) {
      // Get the current and previous values
      const spanCode = state.selectedCode;
      const newValue = REPORT_DATA_OUTPUTS[spanCode];
      const previousValue = initialContent[spanCode] || '';

      // Only proceed if content actually changed
      if (newValue !== previousValue) {
        // Optimistically update UI first
        removeReviewedCode(state.selectedCode);
        setIsEditing(false);


        // Create history entry
        const editEntry = {
          clinicianId: auth.currentUser.uid,
          clinicianName: auth.currentUser.displayName,
          timestamp: serverTimestamp(),
          previousValue,
          newValue,
          editType: "manual",
        };
        const timestampKey = Date.now().toString();

        // Base path to this section. MODULE_NUMBER and timestampKey are numeric
        // segments; Firestore's SDK backtick-escapes digit-leading segments
        // automatically when it builds the update mask, so the dotted-string key
        // form is safe here (and matches handleParagraphApproval exactly).
        const sectionPath = `dataModel.6_manageReports.sections.${MODULE_NUMBER}`;

        // Write ONLY the affected keys with a plain updateDoc rather than a
        // read-modify-write runTransaction. Transactions commit with a
        // document-version precondition; on slow 3G/4G the single patient doc is
        // being touched concurrently (in-flight approvals, auto report/cover
        // re-renders, lastReviewedAt bumps) so the transaction exhausts its
        // retries and throws "the stored version does not match the required
        // base version" — which silently discarded the clinician's edit. A
        // targeted updateDoc has no version precondition and Firestore merges
        // writes to distinct field paths server-side. See handleParagraphApproval
        // for the same reasoning applied to approvals.
        const updateData = {
          // Persist the edited text (targeted, replacing the whole-map rewrite)
          [`${sectionPath}.outputs.${spanCode}`]: newValue,
          // Un-approve this paragraph: editing invalidates a prior approval
          [`${sectionPath}.approvedParagraphs.${spanCode}`]: deleteField(),
          // Append an audit entry under this paragraph's edit history
          [`${sectionPath}.editHistory.${spanCode}.${timestampKey}`]: editEntry,
          // Review metadata (mirrors the approval write)
          [`${sectionPath}.lastReviewedAt`]: serverTimestamp(),
          [`${sectionPath}.reviewedBy`]: auth.currentUser.uid,
          [`${sectionPath}.reviewedByName`]: auth.currentUser.displayName,
        };

        // Perform Firestore update in the background
        const startedAt = performance.now();
        updateDoc(doc(db, 'patients', patientId), updateData).then(() => {
          // Confirms the edit persisted AND the paragraph was un-approved
          // (removed from approvedParagraphs) in Firestore. Code-only, no PII.
          const elapsedMs = Math.round(performance.now() - startedAt);
          console.log(`[edit] saved & UNAPPROVED "${spanCode}" (content updated) in ${elapsedMs}ms`);
          // Analytics mirror of the edit that just persisted: paragraph code,
          // section and the two lengths only — never the wording itself.
          emitParagraphEdited({
            patientId,
            module: MODULE_PREFIX_CODE,
            docStructure,
            code: spanCode,
            prevText: previousValue,
            priorHistory: REPORT_EDIT_HISTORY[spanCode] || {},
            nextText: newValue,
          });
        }).catch(error => {
          console.error("Error updating paragraph approval on edit:", error);
          // Roll back optimistic update on error and surface the failure
          addReviewedCode(state.selectedCode);
          toast.error(
            'Your edit did not save. Please try editing the paragraph again.',
            { toastId: 'paragraph-edit-save-error' }
          );
        });
        // Mark section as dirty since a change was saved
        setSectionDirty(true);
      } else {
        // If no changes, just exit edit mode
        setIsEditing(false);
      }
    }
  };

  // Update handleCancel to revert to pre-edit content
  const handleCancel = () => {
    if (state.selectedCode && preEditContent[state.selectedCode]) {
      // Revert to the content before this edit session started
      if (REPORT_DATA_OUTPUTS) {
        REPORT_DATA_OUTPUTS[state.selectedCode] = preEditContent[state.selectedCode];
      }
    }
    setIsEditing(false);
  };

  const handleReset = () => {
    if (state.selectedCode && initialContent[state.selectedCode]) {
      const spanCode = state.selectedCode;
      const newValue = initialContent[state.selectedCode];
      const previousValue = REPORT_DATA_OUTPUTS[state.selectedCode];

      // Optimistically update UI
      if (REPORT_DATA_OUTPUTS) {
        REPORT_DATA_OUTPUTS[state.selectedCode] = initialContent[state.selectedCode];
      }

      // Remove from reviewed codes optimistically
      removeReviewedCode(state.selectedCode);

      // Create history entry for the reset
      const editEntry = {
        clinicianId: auth.currentUser.uid,
        clinicianName: auth.currentUser.displayName,
        timestamp: serverTimestamp(),
        previousValue,
        newValue,
        isReset: true  // Flag to indicate this was a reset action
      };
      const timestampKey = Date.now().toString();

      // Same targeted-write approach as handleSaveEdit / handleParagraphApproval:
      // a plain updateDoc on specific field paths (no version precondition)
      // instead of a read-modify-write runTransaction, which contends on the
      // single patient doc and fails under slow 3G/4G (losing the reset).
      const sectionPath = `dataModel.6_manageReports.sections.${MODULE_NUMBER}`;
      const updateData = {
        // Restore the original generated text
        [`${sectionPath}.outputs.${spanCode}`]: newValue,
        // A reset also invalidates any prior approval
        [`${sectionPath}.approvedParagraphs.${spanCode}`]: deleteField(),
        // Record the reset in this paragraph's edit history
        [`${sectionPath}.editHistory.${spanCode}.${timestampKey}`]: editEntry,
        // Review metadata (mirrors the approval write)
        [`${sectionPath}.lastReviewedAt`]: serverTimestamp(),
        [`${sectionPath}.reviewedBy`]: auth.currentUser.uid,
        [`${sectionPath}.reviewedByName`]: auth.currentUser.displayName,
      };

      // Perform Firestore update
      const startedAt = performance.now();
      updateDoc(doc(db, 'patients', patientId), updateData).then(() => {
        // Confirms the reset persisted AND the paragraph was un-approved
        // (removed from approvedParagraphs) in Firestore. Code-only, no PII.
        const elapsedMs = Math.round(performance.now() - startedAt);
        console.log(`[reset] reset & UNAPPROVED "${spanCode}" in ${elapsedMs}ms`);
        // Analytics mirror of the reset that just persisted (code only).
        emitParagraphReset({
          patientId,
          module: MODULE_PREFIX_CODE,
          docStructure,
          code: spanCode,
        });
      }).catch(error => {
        console.error("Error resetting paragraph:", error);
        // Roll back optimistic updates on error and surface the failure
        if (REPORT_DATA_OUTPUTS) {
          REPORT_DATA_OUTPUTS[state.selectedCode] = previousValue;
        }
        addReviewedCode(state.selectedCode);
        toast.error(
          'The paragraph reset did not save. Please try again.',
          { toastId: 'paragraph-reset-save-error' }
        );
      });

      setIsEditing(false);
      // Mark section as dirty since content was reset
      setSectionDirty(true);
    }
  };

  // Helper function to check if content has been modified from original
  const hasBeenModified = (code) => {
    if (!code) return false;
    return REPORT_DATA_OUTPUTS[code] !== initialContent[code];
  };

  const [showInitialWarning, setShowInitialWarning] = useState(true);
  // showWarning is provided by useApprovalTracking

  // State for initial content (on page load)
  const [initialContent, setInitialContent] = useState({});

  // Store initial content on page load, only for paragraphs with content
  useEffect(() => {
    if (REPORT_DATA_OUTPUTS) {
      const initialValues = {};
      // Loop through docStructure to get all codes
      Object.values(docStructure).forEach(section => {
        Object.values(section).forEach(({ code }) => {
          // Only store content that exists
          if (code && REPORT_DATA_OUTPUTS[code] !== undefined && REPORT_DATA_OUTPUTS[code] !== "") {
            initialValues[code] = REPORT_DATA_OUTPUTS[code];
          }
        });
      });
      setInitialContent(initialValues);
    }
  }, [REPORT_DATA_OUTPUTS, docStructure]);

  // State for pre-edit content (before current edit session)
  const [preEditContent, setPreEditContent] = useState({});

  const documentContainerRef = useRef(null);

  const [isSaving, setIsSaving] = useState(false);
  const [sectionDirty, setSectionDirty] = useState(false);

  const handleExitReview = async () => {
    setIsSaving(true);

    try {
      // Wait for any optimistic per-paragraph approval writes to commit so the
      // finalisation below reads the true, current approval state from Firestore.
      await settlePendingApprovals();

      // Derive and persist the section approval flag transactionally from the
      // fresh document snapshot (see sectionReviewFinalization). This replaces
      // writing the client's optimistic isFullyApproved, which could diverge
      // from Firestore and finalise an incomplete report.
      const { isFullyApproved: approvedNow, outstandingCodes, manageReports } = await finalizeSectionReview({
        patientId,
        modulePrefix: MODULE_PREFIX_CODE,
        moduleNumber: MODULE_NUMBER,
        docStructure,
      });

      // Reconcile the optimistic UI against the authoritative snapshot before
      // allowing the exit. Stay on the page when either "phantom approvals"
      // exist (paragraphs shown green whose background write actually failed —
      // rapid clicking can overload the per-document transaction queue) or the
      // UI believed everything was approved but the snapshot still found
      // outstanding content. The affected paragraphs are reverted to
      // needs-review and the document re-fetched so the page reflects reality.
      const unpersisted = computeUnpersistedApprovals(reviewedCodes, outstandingCodes);
      if (!approvedNow && (isFullyApproved || unpersisted.length > 0)) {
        unpersisted.forEach(removeReviewedCode);
        await fetchPatientData(patientId);
        toast.error(
          unpersisted.length > 0
            ? `${unpersisted.length} paragraph approval${unpersisted.length === 1 ? ' did' : 's did'} not save. `
              + 'The affected paragraphs have been returned to "needs review" — please approve them again.'
            : 'Some paragraphs still need review before this section can be completed. '
              + 'The page has been refreshed to show them.',
          { toastId: 'review-exit-unsaved' }
        );
        return;
      }

      // Only render when the AUTHORITATIVE (snapshot-derived) approval is true
      // AND this section was actually changed this session.
      const shouldRenderSection = approvedNow && sectionDirty;

      appendReportAuditEntry(patientId, {
        module: MODULE_PREFIX_CODE,
        eventType: 'approved',
        isFirstOccurrence: approvedNow && !manageReports?.qbTestReportFirstApprovedAt,
      });

      if (shouldRenderSection) {
        TriggerRenderDocTemplate({
          reportStage: "QbTest",
          clinicianId: auth.currentUser.uid,
          patientId: patientId
        });
      }

      // Clear any cached data before navigating
      clearPatientData();
      // Reset dirty flag for next session
      setSectionDirty(false);

      // Navigate back to documents page
      navigate(`/patient/${patientId}/documents`);
    } catch (error) {
      console.error('Error during document review completion:', error);
      // Surface the failure so the clinician knows the exit did not complete —
      // silently staying on the page looks like a frozen button.
      toast.error(
        'Could not save your review just now. Please check your connection and try again.',
        { toastId: 'review-exit-error' }
      );
    } finally {
      setIsSaving(false);
    }
  };

  const goToFirstUnreviewed = () => {
    // Only get paragraphs that are actually rendered on the page
    const allParagraphs = document.querySelectorAll('[data-paragraph-container]');
    const firstUnreviewed = Array.from(allParagraphs).find(p => !reviewedCodes.has(p.id));

    if (firstUnreviewed && documentContainerRef.current) {
      const containerRect = documentContainerRef.current.getBoundingClientRect();
      const nextRect = firstUnreviewed.getBoundingClientRect();

      // Calculate scroll position to center the paragraph
      const scrollTop = documentContainerRef.current.scrollTop +
        (nextRect.top - containerRect.top) -
        containerRect.height / 3;

      // Scroll to position
      documentContainerRef.current.scrollTo({
        top: scrollTop,
        behavior: 'smooth'
      });

      // After scroll completes, trigger click handler
      setTimeout(() => {
        const nextCode = firstUnreviewed.id;
        const nextContent = REPORT_DATA_OUTPUTS[nextCode];

        // Trigger the full paragraph click handler
        handleParagraphClick(
          { currentTarget: firstUnreviewed },
          nextCode,
          nextContent
        );
      }, 300);
    }
  };

  useEffect(() => {
    // Apply the overflow: hidden style when the component mounts
    document.body.style.overflow = 'hidden';
    // Clean up the style when the component unmounts
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Add useEffect to fetch model inputs
  useEffect(() => {
    const fetchModelInputs = async () => {
      try {
        const inputs = patientDataModel?.dataModel?.['6_manageReports']?.sections?.[`${MODULE_NUMBER}`]?.inputs;
        if (inputs) {
          setState(prev => ({ ...prev, modelInputsFirestore: inputs }));
        } else {
          console.error('Inputs data is missing');
        }
      } catch (error) {
        console.error('Error fetching model inputs from Firestore:', error);
      }
    };

    if (patientDataModel) {
      fetchModelInputs();
    }
  }, [patientDataModel]);


  // Modify the overlay to explicitly allow scroll events
  useEffect(() => {
    // Store ref in a variable inside the effect
    const currentContainer = documentContainerRef.current;

    if (isEditing && currentContainer) {
      // Save original overflow setting
      const originalOverflow = currentContainer.style.overflow;

      // Ensure the document container is still scrollable during editing
      currentContainer.style.overflow = 'auto';

      // Create a wheel event handler that prevents propagation but allows default behavior
      const handleWheel = (e) => {
        e.stopPropagation();
        // But let the default scroll behavior happen
      };

      // Add the wheel event listener
      currentContainer.addEventListener('wheel', handleWheel, { passive: true });

      // Clean up function uses the stored variable
      return () => {
        currentContainer.removeEventListener('wheel', handleWheel);
        currentContainer.style.overflow = originalOverflow;
      };
    }
  }, [isEditing]);

  const handlePageClick = useCallback((event) => {
    // Ignore clicks if we're in edit mode
    if (isEditing) {
      return;
    }

    // Ignore clicks on paragraphs or the action panel
    if (
      event.target.closest('[data-paragraph-container]') ||
      event.target.closest('[data-action-panel]')
    ) {
      return;
    }
    setIsEditing(false);
    // Reset state
    setState(prev => ({
      ...prev,
      selectedCode: null,
      selectedContent: null,
      isSidePanelOpen: false
    }));
  }, [isEditing]);

  // Add useEffect to set up and clean up click handler
  useEffect(() => {
    // Store ref in a variable inside the effect
    const container = documentContainerRef.current;

    if (container) {
      container.addEventListener('click', handlePageClick);
    }

    return () => {
      // Use the stored variable in cleanup
      if (container) {
        container.removeEventListener('click', handlePageClick);
      }
    };
  }, [handlePageClick]);

  const updateActionPanelPosition = (codeToUse = state.selectedCode) => {
    const activeParagraph = document.querySelector(`[data-paragraph-container][id="${codeToUse}"]`);
    if (activeParagraph && documentContainerRef.current) {
      const containerRect = documentContainerRef.current.getBoundingClientRect();
      const paragraphRect = activeParagraph.getBoundingClientRect();

      setActionPanelPosition({
        top: paragraphRect.bottom - containerRect.top + documentContainerRef.current.scrollTop - 15,
        left: paragraphRect.right - containerRect.left - 275
      });
    }
  };

  const handleParagraphApproval = async (code, isApproving) => {
    try {
      const docRef = doc(db, 'patients', patientId);

      // Base path to this section's approval bookkeeping. MODULE_NUMBER is a
      // numeric section id (e.g. "8"). Firestore's SDK backtick-escapes such
      // digit-leading segments automatically when it builds the update mask,
      // so the dotted-string key form is safe here.
      const sectionPath = `dataModel.6_manageReports.sections.${MODULE_NUMBER}`;

      // Write ONLY this paragraph's key rather than reading the whole document
      // and rewriting the entire approvedParagraphs map inside a
      // read-modify-write transaction.
      //
      // Why: runTransaction uses optimistic concurrency — every commit carries
      // a document-version precondition. When a clinician approves several
      // paragraphs in quick succession (especially on slow 3G/4G links), the
      // queued transactions all contend on the single patient document and most
      // fail with "the stored version does not match the required base version"
      // (failed-precondition). A plain updateDoc on a specific field path has no
      // version precondition, and Firestore merges concurrent writes to
      // distinct field paths server-side, so per-paragraph approvals no longer
      // fight over the document.
      const updateData = {
        [`${sectionPath}.approvedParagraphs.${code}`]: isApproving
          ? {
              approvedAt: serverTimestamp(),
              approvedBy: auth.currentUser.uid,
              approvedByName: auth.currentUser.displayName,
              content: REPORT_DATA_OUTPUTS[code],
            }
          : deleteField(),
        [`${sectionPath}.lastReviewedAt`]: serverTimestamp(),
        [`${sectionPath}.reviewedBy`]: auth.currentUser.uid,
        [`${sectionPath}.reviewedByName`]: auth.currentUser.displayName,
      };

      // Time the write (updateDoc resolves on SERVER ack, so this is the true
      // round-trip) and log code-only — no patient content — to keep it PII-free.
      const startedAt = performance.now();
      await updateDoc(docRef, updateData);
      const elapsedMs = Math.round(performance.now() - startedAt);
      console.log(
        `[approval] ${isApproving ? 'APPROVED' : 'UNAPPROVED'} "${code}" persisted to Firestore in ${elapsedMs}ms`
      );

      // Analytics mirror of the approval change that just persisted
      // (code only). Both directions share the review control that
      // wrote it, so both share this one call.
      emitParagraphApproval({
        patientId,
        module: MODULE_PREFIX_CODE,
        docStructure,
        code,
        assessmentType: 'adhd',
        isApproving,
      });

      // Mark section as dirty due to approval change
      setSectionDirty(true);
    } catch (error) {
      console.error("Error updating paragraph approval:", error);
      console.error("Error details:", {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      // Re-throw so the caller can observe the failure and roll back the
      // optimistic UI — a silently failed approval leaves a paragraph green on
      // screen with no persisted approval behind it, the proven cause of
      // reports finalising with missing content.
      throw error;
    }
  };

  // Approved paragraphs are now loaded by useApprovalTracking

  const handleMarkAsDone = async () => {
    if (!state.selectedCode) {
      console.log('No code selected, cannot mark as done');
      return;
    }

    console.log('Starting handleMarkAsDone for code:', state.selectedCode);
    const paragraphElement = document.getElementById(state.selectedCode);
    if (paragraphElement) {
      try {
        console.log('Calling handleParagraphApproval...');
        // Track the in-flight write so Save & Exit awaits it before finalising.
        trackApproval(
          handleParagraphApproval(state.selectedCode, true).catch(error => {
            console.error('Error in handleParagraphApproval:', error);
            // Roll back the optimistic update and tell the clinician — a
            // silently failed approval is how incomplete sections used to slip
            // through to the finalised report.
            removeReviewedCode(state.selectedCode);
            toast.error(
              'A paragraph approval did not save and has been returned to "needs review". Please approve it again.',
              { toastId: 'paragraph-approval-save-error' }
            );
          })
        );

        // Find next paragraph before updating UI state
        const allParagraphs = document.querySelectorAll('[data-paragraph-container]');
        const currentIndex = Array.from(allParagraphs).findIndex(p => p.id === state.selectedCode);
        const nextUnreviewed = Array.from(allParagraphs).find((p, index) =>
          index > currentIndex && !reviewedCodes.has(p.id)
        );

        // Update reviewed state
        addReviewedCode(state.selectedCode);

        if (nextUnreviewed && documentContainerRef.current) {
          const containerRect = documentContainerRef.current.getBoundingClientRect();
          const nextRect = nextUnreviewed.getBoundingClientRect();

          const scrollTop = documentContainerRef.current.scrollTop +
            (nextRect.top - containerRect.top) -
            containerRect.height / 3;

          // Then scroll
          documentContainerRef.current.scrollTo({
            top: scrollTop,
            behavior: 'smooth'
          });

          setTimeout(() => {
            const nextCode = nextUnreviewed.id;

            // Update state with new paragraph
            setState(prev => ({
              ...prev,
              selectedCode: nextCode,
              isSidePanelOpen: true,
              article: state.modelInputsFirestore[nextCode]["Article"],
              clinicianInputs: state.modelInputsFirestore[nextCode]["Clinician Notes"],
              // clinicianDecisions: state.modelInputsFirestore[nextCode]["Clinician Decisions"],
              // checkedExamples: state.modelInputsFirestore[code]["Checked Examples"],
              patientInputs: state.modelInputsFirestore[nextCode]["Patient Prefill"],
              // informantInputs: state.modelInputsFirestore[nextCode]["Informant Prefill"]
            }));

            // Pass the new code directly to position update
            setTimeout(() => updateActionPanelPosition(nextCode), 0);
          }, 300);
        } else {
          // This was the last paragraph - unselect it
          setState(prev => ({
            ...prev,
            selectedCode: null,
            isSidePanelOpen: false,
            clinicianInputs: null,
          }));
        }
      } catch (error) {
        console.error('Error in handleMarkAsDone:', error);
        console.error("Error details:", {
          code: error.code,
          message: error.message,
          stack: error.stack
        });
      }
    }
  };

  // Update handleSkip with the same scroll logic
  const handleSkip = () => {
    if (!state.selectedCode) return;

    const paragraphElement = document.getElementById(state.selectedCode);
    if (paragraphElement) {
      // Find next paragraph BEFORE updating UI state
      const allParagraphs = document.querySelectorAll('[data-paragraph-container]');
      const currentIndex = Array.from(allParagraphs).findIndex(p => p.id === state.selectedCode);
      const nextUnreviewed = Array.from(allParagraphs).find((p, index) =>
        index > currentIndex && !reviewedCodes.has(p.id)
      );

      if (nextUnreviewed && documentContainerRef.current) {
        const containerRect = documentContainerRef.current.getBoundingClientRect();
        const nextRect = nextUnreviewed.getBoundingClientRect();

        const scrollTop = documentContainerRef.current.scrollTop +
          (nextRect.top - containerRect.top) -
          containerRect.height / 3;

        // Then scroll
        documentContainerRef.current.scrollTo({
          top: scrollTop,
          behavior: 'smooth'
        });

        setTimeout(() => {
          const nextCode = nextUnreviewed.id;

          // Update state with new paragraph
          setState(prev => ({
            ...prev,
            selectedCode: nextCode,
            isSidePanelOpen: true,
            article: state.modelInputsFirestore[nextCode]["Article"],
            clinicianInputs: state.modelInputsFirestore[nextCode]["Clinician Notes"],
            // clinicianDecisions: state.modelInputsFirestore[nextCode]["Clinician Decisions"],
            patientInputs: state.modelInputsFirestore[nextCode]["Patient Prefill"],
            // informantInputs: state.modelInputsFirestore[nextCode]["Informant Prefill"]
          }));

          // Pass the new code directly to position update
          setTimeout(() => updateActionPanelPosition(nextCode), 0);
        }, 300);
      } else {
        // This was the last paragraph - unselect it
        setState(prev => ({
          ...prev,
          selectedCode: null,
          isSidePanelOpen: false,
          clinicianInputs: null,
        }));
      }
    }
  };

  // Add progress calculation
  const calculateProgress = useCallback(() => {
    if (totalParagraphs === 0) return 0;
    return Math.round((reviewedCodes.size / totalParagraphs) * 100);
  }, [reviewedCodes.size, totalParagraphs]);

  return (
    <Layout activePage="dashboard" scrollable={false}>
      <InitialWarningDialog showInitialWarning={showInitialWarning} setShowInitialWarning={setShowInitialWarning} />
      <div className="h-full flex flex-col overflow-hidden  ">

        {/* // Add overlay component when editing */}
        {isEditing && (
          <div
            className="fixed inset-0 bg-gray-900/20 z-10 transition-opacity duration-200"
            style={{
              pointerEvents: 'none' // Allow clicks to pass through to underlying elements
            }}
          />
        )}

        {/* Fixed header section */}
        <div className="flex-none pt-4">
          <DocumentReviewHeader
            title={`Reviewing: ${MODULE_TITLE}`}
            reviewedCodes={reviewedCodes}
            totalParagraphs={totalParagraphs}
            isSaving={isSaving}
            handleExitReview={handleExitReview}
            goToFirstUnreviewed={goToFirstUnreviewed}
            calculateProgress={calculateProgress}
          />
          <WarningBar show={showWarning} />
        </div>

        {/* Scrollable document section */}
        <div className="flex-1 px-8 pb-8 min-h-0 relative">
          <div className="document-display-container h-full overflow-hidden bg-slate-800">
            {/* Main flex container for document and side panel */}
            <div className="flex h-full">
              {/* Left side - Document container */}
              <div
                ref={documentContainerRef}
                className={`
                  w-1/2 relative m-4 p-16 border shadow-md bg-white
                  ${isEditing ? 'overflow-hidden' : 'overflow-y-auto'}
                `}
              >
                <div className="space-y-8 text-left">
                  {Object.entries(docStructure).map(([title, paragraphs]) => (
                    <Section
                      key={title}
                      title={title}
                      paragraphs={paragraphs}
                    />
                  ))}
                </div>
                <div className="h-[calc(100vh-32rem)]" aria-hidden="true" />

                {/* Floating Action Panel */}
                {state.selectedCode && (
                  <div
                    data-action-panel
                    className={`
                        absolute z-20 bg-gray-100 rounded-xl shadow border-2 border-gray-300 p-2
                        transition-all duration-300 ease-in-out
                      `}
                    style={{
                      top: `${actionPanelPosition.top}px`,
                      left: `${actionPanelPosition.left}px`,
                      transform: 'translateY(8px)',
                    }}
                  >
                    <div className="font-bold flex items-center gap-2">
                      {isEditing ? (
                        <>
                          <button
                            onClick={handleSaveEdit}
                            className="px-2 py-1 rounded-lg text-sm flex items-center justify-center bg-amber-200 hover:bg-amber-300 border border-gray-300"
                          >
                            <SaveIcon className="w-5 h-5 mr-1" />
                            Save
                          </button>
                          <button
                            onClick={handleCancel}
                            className="px-2 py-1 rounded-lg text-sm flex items-center justify-center bg-red-500 text-white hover:bg-red-600"
                          >
                            <XIcon className="w-5 h-5 mr-1" />
                            Cancel
                          </button>
                          {hasBeenModified(state.selectedCode) && (
                            <button
                              onClick={handleReset}
                              className="px-2 py-1 rounded-lg text-sm flex items-center justify-center bg-black text-white hover:bg-gray-800"
                            >
                              <RefreshIcon className="w-5 h-5 mr-1" />
                              Reset
                            </button>
                          )}
                        </>
                      ) : (
                        <>
                          <button
                            onClick={handleStartEdit}
                            className="px-2 py-1 rounded-lg text-sm flex items-center justify-center bg-amber-200 hover:bg-amber-300 border border-gray-300"
                          >
                            <PencilIcon className="w-5 h-5 mr-1" />
                            Edit
                          </button>
                          <button
                            onClick={handleMarkAsDone}
                            className="px-2 py-1 rounded-lg text-sm flex items-center justify-center bg-green-600 text-white hover:bg-green-500"
                          >
                            <CheckIcon className="w-5 h-5 mr-1" />
                            Approve
                          </button>
                          <button
                            onClick={handleSkip}
                            className="px-2 py-1 rounded-lg text-sm flex items-center justify-center bg-gray-100 hover:bg-gray-200 border border-gray-300"
                          >
                            <FastForwardIcon className="w-5 h-5 mr-1" />
                            Skip
                          </button>
                        </>
                      )}

                    </div>
                  </div>
                )}
              </div>

              {/* Right side - Side Panel Container */}
              <div className={`
                  w-2/5 p-4 flex flex-col
                  ${!state.selectedCode ? 'hidden' : ''}
                `}>
                <div className={`
                    flex-1 text-sm bg-gray-100 rounded-xl overflow-y-auto 
                    border shadow-sm
                    flex flex-col h-full
                  `}>
                  {state.selectedCode && (
                    <div className="flex flex-col h-full p-6 overflow-hidden">
                      <div className="flex-1 flex flex-col min-h-0">
                        <div className="flex-none flex justify-between items-center">
                          <p className="text-xl font-bold text-gray-800 mb-2">AI Inputs</p>
                        </div>
                        <div className="overflow-y-auto">
                          {renderModelInput("Your decisions", state.clinicianDecisions)}
                          {renderModelInput("Your notes", state.clinicianInputs)}
                          {renderModelInput("Your checked examples", state.checkedExamples)}
                          {renderModelInput("Patient's report", state.patientInputs)}
                          {renderModelInput("Informant comments", state.informantInputs)}
                          {renderModelInput("ThinkDivergent evidence-based article", state.article)}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}